<!--
  @deprecated Please use `TmCustomTooltip` or v-tooltip directive instead.
-->
<template>
  <q-tooltip-fixed
    ref="qTooltip"
    v-bind="attrs"
    class="tm-tooltip"
    :class="[
      wrapperClass,
      {
        'tm-tooltip--white': white,
        'tm-tooltip--hide-arrow': hideArrow,
        'tm-tooltip--full-filled': fullFilled,
        'tm-tooltip--unset-max-height': unsetMaxHeight, // not used
        'tm-tooltip--pointer-event-auto': isAllowPointerEvents,
        'tm-tooltip--default-padding': isDefaultPadding,
      },
    ]"
    :model-value="modelValue"
    :no-parent-event="noParentEvent"
    :max-width="maxWidth"
    :delay="delayed ? delayTime : 0"
    :hide-delay="hideDelay"
    transition-show="fade"
    transition-hide="fade"
    :on-update:model-value="updateModelValueHandler"
    @before-show="beforeShow"
    @before-hide="beforeHide"
    @hide="hide"
  >
    <div
      ref="innerMenuRef"
      class="tm-tooltip__main"
      :class="contentClass"
    >
      <slot>{{ text }}</slot>
    </div>
  </q-tooltip-fixed>
</template>

<script setup lang="ts">
/**
 * @deprecated Please use `TmCustomTooltip` or v-tooltip directive instead.
 */
import { computed, nextTick, useTemplateRef, useAttrs } from '@/composition/vue/compositionApi'
import type { ControlPosition } from '@/composition/tooltip/renderTooltip'
import { getTooltipAngleAttrs, useRenderTooltip } from '@/composition/tooltip/renderTooltip'
import { delayOptions } from '@/components/shared/types'
import { QTooltipFixed } from '@/components/shared/tooltip/QTooltipFixed'
import type { VueLikeClassAttr } from '@/components/types'
import type { StylesheetSizeProp } from '@/types'
import { getLoggerService } from '@/core/container/ioc'
import TmLogicError from '@/core/error/tmLogicError'

const props = withDefaults(
  defineProps<{
    modelValue?: boolean
    text?: string
    maxWidth?: StylesheetSizeProp
    wrapperClass?: string | object
    delayed?: boolean
    delayTime?: number
    hideDelay?: number
    white?: boolean
    hideArrow?: boolean
    fullFilled?: boolean
    unsetMaxHeight?: string
    target?: HTMLElement | boolean | string
    offset?: number[]
    offsetWithAngle?: boolean
    position?: ControlPosition
    contentClass?: VueLikeClassAttr
    isAllowPointerEvents?: boolean
    isDefaultPadding?: boolean
    noParentEvent?: boolean
  }>(),
  {
    maxWidth: '285px',
    delayTime: delayOptions.default,
    offsetWithAngle: true,
    position: 'top',
    contentClass: 'text-left',
    isAllowPointerEvents: false,
    isDefaultPadding: true,
  },
)

const emit = defineEmits<{
  show: [menuEl: HTMLDivElement]
  'before-hide': []
  // update:modelValue event usage from attrs
  // 'update:modelValue': [value: boolean]
}>()

const qTooltip = useTemplateRef<InstanceType<typeof QTooltipFixed>>('qTooltip')
const innerMenuRef = useTemplateRef<HTMLDivElement>('innerMenuRef')
const { show, hide } = useRenderTooltip('tm-tooltip')
const angleAttrs = computed(() => getTooltipAngleAttrs('tm-tooltip', props.position, props.offsetWithAngle))
const inheritedAttrs = useAttrs()

const attrs = computed(() => ({
  ...angleAttrs.value,
  ...inheritedAttrs,
  offset: props.offset || angleAttrs.value.offset,
}))

const updateModelValueHandler = computed(() => {
  const listener = inheritedAttrs['onUpdate:modelValue']
  if (!listener) {
    return undefined
  }

  if (typeof listener !== 'function') {
    throw new TmLogicError(`listener should be type "function". Actual type "${typeof listener}}"`)
  }

  return (value: boolean) => listener(value)
})

const tooltipShow = () => {
  qTooltip.value?.show()
}

const tooltipHide = () => {
  qTooltip.value?.hide()
}

const tooltipToggle = () => {
  qTooltip.value?.toggle()
}

defineExpose({
  show: tooltipShow,
  hide: tooltipHide,
  toggle: tooltipToggle,
})

const getAnchorEl = (el: HTMLElement | null) => {
  let anchorEl = el
  while (anchorEl?.classList.contains('q-anchor--skip')) {
    anchorEl = anchorEl?.parentElement as HTMLElement
  }
  return anchorEl
}

const beforeShow = async () => {
  const anchorEl: HTMLElement | null =
    (props.target instanceof HTMLElement ? props.target : null) ?? getAnchorEl(qTooltip.value?.$el?.parentElement)
  if (!anchorEl) {
    return
  }
  // for correct angle display need event between before-show and show, one nextTick not enough
  await nextTick()
  await nextTick()
  const menuEl = innerMenuRef.value?.parentElement as HTMLDivElement

  if (!menuEl || !anchorEl) {
    getLoggerService().log('warn', '"menuEl" or "anchorEl" is missing')
    return
  }

  emit('show', menuEl)
  show({
    anchorEl,
    menuEl,
    attrs: {
      ...angleAttrs.value,
      offset: props.offset || angleAttrs.value.offset,
    },
  })
}

const beforeHide = () => {
  emit('before-hide')
}
</script>

<!-- eslint-disable-next-line vue/enforce-style-attribute -->
<style lang="scss">
// Here we have non-scoped style because dropdown appears at the end on body without special uniq attr
// so scoped styles doesn't work
@import '@/styles/mixins.scss';

.tooltip-title {
  font-weight: $bold-font;
  padding-bottom: 2px;
}
.w270 {
  width: 270px;
}
.w420 {
  width: 420px;
}
.pa-10px {
  padding: 10px !important;
}
.tm-tooltip {
  @include offset-tooltip();
}
.tm-tooltip {
  --angle-color: #{$gray9};
  --angle-border-color: transparent;
  display: flex !important;
  flex-flow: column;
  overflow: unset !important;
  box-shadow: unset !important;
  background: unset !important;
  border-radius: unset !important;

  &.white,
  &--white {
    --angle-color: #{$white};
    --angle-border-color: #{$gray3};
  }

  &--full-filled {
    width: 100%;
  }

  &--unset-max-height {
    max-height: unset !important;
  }

  /* Fix production CSS order, because .tooltip has more specific no-pointer-events class */
  &.q-tooltip.tm-tooltip--pointer-event-auto {
    pointer-events: auto !important;
  }

  &--default-padding {
    .tm-tooltip__main {
      padding: 4px 8px;
    }
  }

  &__main {
    position: relative;
    width: 100%;
    font-weight: $regular-font;
    font-size: $sm-x-font;
    line-height: 20px;
    word-break: break-word;
    color: $white;
    box-shadow: $box-shadow;
    border-radius: $border-radius;
    background: $gray9;
    overflow: auto;

    .tm-tooltip.white &,
    .tm-tooltip--white & {
      color: $neutral;
      font-weight: $regular-font;
      background: $white;
    }
  }
}
</style>
